import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { motion } from "framer-motion";
import {
  ChartLineUp, Heartbeat, Lightning, TrendUp, CalendarDots, Info,
  BookOpen, Crown, Sparkle, Tag,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import PremiumUpgrade, { PremiumBadge } from "../components/PremiumUpgrade";
import WhatToWear from "../components/WhatToWear";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_COLORS = {
  happy: "#F4A261", calm: "#84A98C", energized: "#E9C46A", stressed: "#E76F51",
  anxious: "#CDB4DB", sad: "#457B9D", tired: "#A8DADC", frustrated: "#E07A5F",
  grateful: "#2D6A4F", hopeful: "#FFD166",
  peaceful: "#2D6A4F", refreshed: "#F4A261", grounded: "#457B9D",
  lighter: "#CDB4DB", same: "#78857F", restless: "#E07A5F",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const isPremium = user?.is_premium;
  const [history, setHistory] = useState([]);
  const [insights, setInsights] = useState(null);
  const [journalInsights, setJournalInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [histRes, insightRes, journalRes] = await Promise.all([
          axios.get(`${API}/mood/history?limit=30`, { withCredentials: true }),
          axios.get(`${API}/mood/insights`, { withCredentials: true }),
          axios.get(`${API}/mood/journal-insights`, { withCredentials: true }),
        ]);
        setHistory(histRes.data);
        setInsights(insightRes.data);
        setJournalInsights(journalRes.data);
      } catch (err) {
        console.error("Failed to load dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const chartData = [...history].reverse().map((entry) => {
    const date = new Date(entry.created_at);
    return {
      date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      intensity: entry.intensity,
      mood: entry.mood,
    };
  });

  const pieData = insights?.mood_distribution
    ? Object.entries(insights.mood_distribution).map(([mood, count]) => ({
        name: mood.charAt(0).toUpperCase() + mood.slice(1),
        value: count,
        color: MOOD_COLORS[mood] || "#78857F",
      }))
    : [];

  const moodShiftData = journalInsights?.mood_shifts?.map((s) => ({
    name: s.mood.charAt(0).toUpperCase() + s.mood.slice(1),
    count: s.count,
    fill: MOOD_COLORS[s.mood] || "#84A98C",
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 rounded-full bg-[#2D6A4F] animate-breathe" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-2">Mood Tracking</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2421] tracking-tight">
            Your Wellness Dashboard
          </h1>
        </div>
        {!isPremium && (
          <button
            onClick={() => setShowPremiumModal(true)}
            data-testid="upgrade-button-header"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F4A261]/10 text-[#F4A261] text-sm font-semibold hover:bg-[#F4A261]/20 transition-all"
          >
            <Crown size={16} weight="fill" />
            Upgrade
          </button>
        )}
        {isPremium && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F4A261]/10 text-[#F4A261] text-xs font-bold">
            <Crown size={14} weight="fill" /> Premium
          </span>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Check-ins", value: insights?.total_entries || 0, icon: Heartbeat, color: "#2D6A4F" },
          { label: "Avg Intensity", value: insights?.average_intensity || 0, icon: Lightning, color: "#F4A261" },
          { label: "Journal Entries", value: journalInsights?.total_journals || 0, icon: BookOpen, color: "#84A98C" },
          { label: "This Week", value: history.filter((e) => {
              const d = new Date(e.created_at);
              const now = new Date();
              return (now - d) < 7 * 24 * 60 * 60 * 1000;
            }).length, icon: CalendarDots, color: "#CDB4DB" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="card-neuro p-5"
            data-testid={`stat-card-${i}`}
          >
            <stat.icon size={22} weight="duotone" style={{ color: stat.color }} className="mb-2" />
            <p className="font-['Outfit'] text-2xl font-medium text-[#1A2421]">{stat.value}</p>
            <p className="text-xs text-[#78857F]">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Mood Trend */}
        <div className="card-neuro p-6 lg:col-span-2" data-testid="mood-trend-chart">
          <div className="flex items-center gap-2 mb-4">
            <ChartLineUp size={20} weight="duotone" className="text-[#2D6A4F]" />
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421]">Mood Intensity Trend</h3>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorIntensity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2D6A4F" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2D6A4F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E1D9CD" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#78857F" }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#78857F" }} />
                <Tooltip
                  contentStyle={{
                    background: "#fff", border: "1px solid #E1D9CD",
                    borderRadius: "12px", fontSize: "12px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="intensity"
                  stroke="#2D6A4F"
                  strokeWidth={2}
                  fill="url(#colorIntensity)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[240px] text-sm text-[#78857F]">
              Complete mood scans to see trends here
            </div>
          )}
        </div>

        {/* Mood Distribution */}
        <div className="card-neuro p-6" data-testid="mood-distribution-chart">
          <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-4">Mood Distribution</h3>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2">
                {pieData.map((d) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-[10px] text-[#78857F]">{d.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[180px] text-sm text-[#78857F]">
              No data yet
            </div>
          )}
        </div>
      </div>

      {/* ── What to Wear Today ── */}
      {history.length > 0 && (
        <div className="card-neuro p-6 mb-8" data-testid="dashboard-what-to-wear">
          <WhatToWear mood={history[0]?.mood} compact={false} />
        </div>
      )}

      {/* ── Journal Analytics Section ── */}
      {journalInsights && journalInsights.total_journals > 0 && (
        <div className="mb-8" data-testid="journal-analytics-section">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={20} weight="duotone" className="text-[#84A98C]" />
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421]">Journal Analytics</h3>
            {!isPremium && <PremiumBadge onClick={() => setShowPremiumModal(true)} />}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Post-Session Mood Shifts */}
            {moodShiftData.length > 0 && (
              <div className="card-neuro p-6" data-testid="mood-shift-chart">
                <h4 className="text-sm font-semibold text-[#1A2421] mb-3">Post-Session Mood States</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={moodShiftData} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#78857F" }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#78857F" }} width={80} />
                    <Tooltip
                      contentStyle={{
                        background: "#fff", border: "1px solid #E1D9CD",
                        borderRadius: "12px", fontSize: "12px",
                      }}
                    />
                    <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                      {moodShiftData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Journal Themes / Word Cloud */}
            {journalInsights.themes?.length > 0 && (
              <div className="card-neuro p-6" data-testid="journal-themes-card">
                <h4 className="text-sm font-semibold text-[#1A2421] mb-3">Journal Themes</h4>
                <div className="flex flex-wrap gap-2">
                  {journalInsights.themes.map((t, i) => {
                    const size = Math.max(0.7, Math.min(1.3, t.count / 3));
                    return (
                      <motion.span
                        key={t.word}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.04 }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E1D9CD] text-[#1A2421]"
                        style={{ fontSize: `${size}rem` }}
                        data-testid={`theme-tag-${t.word}`}
                      >
                        <Tag size={12} weight="duotone" className="text-[#84A98C]" />
                        {t.word}
                        <span className="text-[10px] text-[#78857F] ml-1">{t.count}</span>
                      </motion.span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* AI-Powered Patterns — Premium */}
          {isPremium && journalInsights.patterns?.length > 0 && (
            <div className="mt-6" data-testid="journal-patterns-section">
              <div className="flex items-center gap-2 mb-3">
                <Sparkle size={18} weight="duotone" className="text-[#CDB4DB]" />
                <h4 className="text-sm font-semibold text-[#1A2421]">AI Pattern Insights</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {journalInsights.patterns.map((p, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    className="card-neuro p-4 flex items-start gap-3"
                    data-testid={`pattern-card-${i}`}
                  >
                    <Sparkle size={16} weight="duotone" className="text-[#F4A261] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#1A2421]">{p}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {!isPremium && (
            <div className="mt-6 card-neuro p-6 text-center relative overflow-hidden" data-testid="premium-upsell-patterns">
              <div className="absolute inset-0 bg-gradient-to-r from-[#F4A261]/5 to-[#CDB4DB]/5" />
              <div className="relative">
                <Sparkle size={24} weight="duotone" className="text-[#F4A261] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#1A2421] mb-1">Unlock AI Pattern Insights</p>
                <p className="text-xs text-[#78857F] mb-3 max-w-sm mx-auto">
                  Premium members get personalized analytics like "You mention 'tension' most on Mondays" and mood improvement trends.
                </p>
                <button
                  onClick={() => setShowPremiumModal(true)}
                  data-testid="upgrade-patterns-button"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2D6A4F] text-white text-sm font-medium hover:bg-[#1B4936] transition-all hover:-translate-y-0.5 shadow-md"
                >
                  <Crown size={14} weight="fill" />
                  Upgrade to Premium — $14.99/mo
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mood Insights */}
      {insights?.insights && insights.insights.length > 0 && (
        <div className="mb-8" data-testid="insights-section">
          <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-4">Mood Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {insights.insights.map((text, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="card-neuro p-4 flex items-start gap-3"
                data-testid={`insight-card-${i}`}
              >
                <Info size={20} weight="duotone" className="text-[#84A98C] mt-0.5 shrink-0" />
                <p className="text-sm text-[#1A2421]">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Entries */}
      {history.length > 0 && (
        <div data-testid="recent-entries-section">
          <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-4">Recent Check-ins</h3>
          <div className="space-y-3">
            {history.slice(0, 5).map((entry) => (
              <div key={entry.id} className="card-neuro p-4 flex items-center gap-4" data-testid={`entry-${entry.id}`}>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: (MOOD_COLORS[entry.mood] || "#78857F") + "20" }}
                >
                  <span className="text-xs font-bold" style={{ color: MOOD_COLORS[entry.mood] }}>
                    {entry.intensity}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A2421] capitalize">{entry.mood}</p>
                  <p className="text-xs text-[#78857F] truncate">
                    {entry.feelings?.join(", ") || "No feelings tagged"}
                  </p>
                </div>
                <span className="text-[10px] text-[#78857F] shrink-0">
                  {new Date(entry.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <PremiumUpgrade
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
      />
    </div>
  );
}
